import { FC, useState } from 'react';
import { FaChevronRight } from '@react-icons/all-files/fa6/FaChevronRight';
import { IoIosArrowDown } from '@react-icons/all-files/io/IoIosArrowDown';
import classNames from 'classnames';

import { Alert } from 'components/design-system/Alert';
import { defaultMaxLength, Input, inputStyle } from 'components/design-system/Input';
import { SlideoutCloseButton } from 'components/design-system/Slideout';
import { Spinner } from 'components/design-system/Spinner';
import { Text } from 'components/design-system/Text';
import { toaster } from 'components/design-system/Toaster';
import { pollListing } from 'components/listing_detail/v2/useRefreshListing';
import { ApplicableTaxesNote } from 'components/offers/ApplicableTaxesNote';
import { COLORS } from 'consts';
import { useBezelUser } from 'context/bezelUser';
import { useModal } from 'context/modal/ModalContext';
import { useLogBidSubmitted } from 'hooks/analytics/useLogMaxBidSubmitted';
import { clientApiPatch } from 'lib/api_helper';
import auctionStyles from 'styles/Auctions.module.scss';
import styles from 'styles/Offer.module.scss';
import { Listing, ProxyBid, ProxyBidStatus } from 'types';
import { formatPrice } from 'utils/text-helpers';

import { ActionButton } from './ActionButton';
import { AllBidsModal } from './AllBidsModal';
import { BidSubmittedModal } from './BidSubmittedModal';
import { OutbidModal } from './OutbidModal';
import { PlaceBidModalCountdown } from './PlaceBidModalCountdown';

import { BuyersPremiumInfo } from '../BuyersPremiumInfo';
import { fetchAndValidateProxyBids } from '../helpers';

const inputId = 'place-bid-input';

export const PlaceBidModalContentTestIds = {
  container: 'place-bid-modal-content-container',
  maxBidExplanation: 'place-bid-modal-content-max-bid-explanation'
};

type PlaceBidModalContentProps = {
  showMaxBidExplanation: () => void;
  closeModal: () => void;
  onSubmit: () => void;
  listing: Listing;
  bidValue: number;
  proxyBid?: ProxyBid;
  refetchProxyBids: () => void;
  setBidValue: (value: number) => void;
  refreshListing: (listing: Listing) => void;
};

export const PlaceBidModalContent: FC<PlaceBidModalContentProps> = ({
  showMaxBidExplanation,
  closeModal,
  onSubmit,
  listing,
  bidValue,
  setBidValue,
  refreshListing,
  proxyBid,
  refetchProxyBids
}) => {
  const [error, setError] = useState<string | null>(null);
  const [warningText, setWarningText] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { closeModal: closeAllBidsModal, openModal } = useModal();
  const { user } = useBezelUser();
  const poll = pollListing(listing.id, refreshListing, listing);
  const logBidSubmitted = useLogBidSubmitted(listing, bidValue);

  const openBidSubmittedModal = () =>
    openModal(<BidSubmittedModal bidValue={bidValue} closeModal={closeModal} listing={listing} proxyBid={proxyBid} />);
  const openOutbidModal = (updatedListing: Listing) =>
    openModal(<OutbidModal closeModal={closeModal} listing={updatedListing} proxyBid={proxyBid} />);

  const openAllBidsModal = () =>
    openModal(
      <AllBidsModal listing={listing} bidValue={bidValue} closeModal={closeAllBidsModal} proxyBid={proxyBid} />
    );
  const openProxyBid =
    proxyBid &&
    proxyBid.listing.id === listing.id &&
    (proxyBid.status === ProxyBidStatus.ACTIVE || proxyBid.status === ProxyBidStatus.EXHAUSTED)
      ? proxyBid
      : null;

  const updateBid = async () => {
    setIsLoading(true);

    try {
      await clientApiPatch(`/api/proxyBids?proxyBidId=${openProxyBid?.id}`, { maxPriceCents: bidValue });

      if (!user?.buyerProfile?.id) {
        throw new Error('Failed to update proxy bid. User is not authenticated');
      }

      const { result, proxyBid: newProxyBid } = await fetchAndValidateProxyBids(user?.buyerProfile?.id, listing.id);

      if (result === 'success') {
        openBidSubmittedModal();
        logBidSubmitted();
      } else if (result === 'outbid') {
        openOutbidModal(newProxyBid?.listing);
      } else if (result === 'error') {
        throw new Error('Failed to update proxy bid');
      }
    } catch (updateError) {
      console.error(updateError);
      toaster.error('Updating bid failed. Please try again later');
    } finally {
      setIsLoading(false);
      poll();
      refetchProxyBids();
    }
  };

  const minimalBid = listing?.auctionInfo.nextBidCents || 0;

  const onChangeOfferPrice = (value: string) => {
    setError(null);
    setBidValue(+value.replace(/\D/g, '') * 100);
    if (warningText || !value) {
      setWarningText(null);
    }
  };

  const onInputBlur = () => {
    if (bidValue < minimalBid) {
      setWarningText('You can only submit a bid \n higher then required bid next step');
    }
  };

  const formattedPrice = formatPrice(bidValue);
  const showWarningText = bidValue !== 0 && warningText;
  const formattedPriceValue = formattedPrice.slice(1);
  // calculate input width based on number of placeholder characters
  const hasPlaceholder = formattedPriceValue === '0';
  const inputWidth = hasPlaceholder ? (String(minimalBid).length - 2) * 44 : 'auto';

  return (
    <div
      data-testid={PlaceBidModalContentTestIds.container}
      className='riforma-regular text-center h-100 pt-2 pb-5 text-light d-flex flex-column justify-content-between align-items-center'
    >
      <div className='w-100'>
        <div className='d-flex justify-content-end w-100'>
          <SlideoutCloseButton isDark onClick={closeModal} />
        </div>
        <div className='fs-18px fs-sm-20px mb-4'>
          <span className='opacity-80'>Place a </span>
          <span
            role='button'
            data-testid={PlaceBidModalContentTestIds.maxBidExplanation}
            onClick={showMaxBidExplanation}
            style={{ borderBottom: `1px solid ${COLORS.primaryLight}` }}
          >
            max bid <IoIosArrowDown size={18} />{' '}
          </span>
        </div>
        <PlaceBidModalCountdown endDate={listing.auctionInfo.endDate} />
      </div>
      <div>
        {error && (
          <Alert key='danger' variant='danger' className='riforma-regular fs-12px mw-100'>
            {error}
          </Alert>
        )}
        <div className={classNames('my-5', styles['new-offer-price-container'])}>
          <div
            role='button'
            style={{ transition: 'all 300ms' }}
            className={classNames('fs-12px text-secondary', {
              'opacity-0': !hasPlaceholder
            })}
            onClick={() => onChangeOfferPrice(formatPrice(minimalBid))}
          >
            MUST BID AT LEAST
          </div>
          <label htmlFor={inputId} className='d-flex align-items-center justify-content-center'>
            <Text
              className='riforma-light text-light opacity-40'
              color={showWarningText ? 'danger' : 'primary'}
              style={inputStyle}
            >
              {formattedPrice[0]}
            </Text>
            <Input
              isResponsive
              autoFocus
              id={inputId}
              value={hasPlaceholder ? '' : formattedPriceValue}
              setValue={onChangeOfferPrice}
              className={classNames('text-center border-0', auctionStyles.input)}
              onBlur={onInputBlur}
              style={{ ...inputStyle, width: inputWidth }}
              isError={!!showWarningText}
              placeholder={formatPrice(minimalBid).slice(1)}
              maxLength={defaultMaxLength}
            />
          </label>
        </div>
        {showWarningText && (
          <Text color='danger' className='white-space-break text-center mb-1' style={{}}>
            {warningText}
          </Text>
        )}
      </div>
      <div className='d-flex flex-column align-items-center justify-content-between w-100 my-3'>
        <BuyersPremiumInfo isDark className='text-secondary fs-14px' />
        <ApplicableTaxesNote color='light' className='mb-4' />
        {openProxyBid && (
          <div
            className='d-flex justify-content-between align-items-center text-start border border-light6 rounded-2 p-3 w-100 bg-light2'
            style={{ marginBottom: '0.75rem' }}
          >
            <div>
              <div className='text-light60 label'>YOUR MAX BID</div>
              <div className='text-light riforma-regular pt-1'>{formatPrice(openProxyBid.maxPriceCents)}</div>
            </div>
          </div>
        )}
        {listing?.auctionInfo.highestBid?.priceCents && (
          <div
            onClick={openAllBidsModal}
            role='button'
            className='d-flex justify-content-between align-items-center text-start border border-light6 rounded-2 p-3 w-100 bg-light2'
          >
            <div>
              <div className='text-light60 label'>CURRENT BID</div>
              <div className='text-light riforma-regular pt-1'>
                {formatPrice(listing?.auctionInfo?.highestBid?.priceCents)}
              </div>
            </div>
            <FaChevronRight className='me-2' />
          </div>
        )}
      </div>
      <ActionButton
        className='w-100'
        disabled={!!warningText || bidValue < minimalBid || isLoading}
        type='primary'
        onClick={openProxyBid ? updateBid : onSubmit}
        content={
          isLoading ? (
            <div className='d-flex h-100 align-items-center justify-content-center'>
              <Spinner />
            </div>
          ) : (
            <div className='w-100 d-flex justify-content-between align-items-center px-4'>
              <div />
              <div>{openProxyBid ? 'Increase your bid' : 'Next'}</div>
              {openProxyBid ? <div /> : <FaChevronRight />}
            </div>
          )
        }
      />
    </div>
  );
};
